import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import apiClient from "@/lib/api-client";
import type { PaperHighlight } from "./useHighlights";

export interface AnnotationPreview {
  text: string;
  page: number | null;
  rects: Array<{ x: number; y: number; width: number; height: number; pageIndex: number }> | null;
  source: "imported";
  has_citation?: boolean;
}

interface BatchResponse {
  created: PaperHighlight[];
  skipped: number;
}

export function useAnnotationImport(
  paperId: string | undefined,
  conceptId: string | undefined,
) {
  const qc = useQueryClient();
  const [previews, setPreviews] = useState<AnnotationPreview[]>([]);
  const [previewSelected, setPreviewSelected] = useState<Set<number>>(new Set());
  const [isPreviewing, setIsPreviewing] = useState(false);

  const scanMutation = useMutation({
    mutationFn: async () => {
      if (!paperId || !conceptId) throw new Error("Missing paperId or conceptId");
      return apiClient
        .post(`api/concepts/${conceptId}/papers/${paperId}/extract-highlights`, {
          searchParams: { preview: "true" },
        })
        .json<AnnotationPreview[]>();
    },
    onSuccess: (data) => {
      setPreviews(data);
      // Auto-select only annotations with nearby citations (smart default for trawling).
      // User can still select/deselect any annotation manually.
      const withCitations = new Set(
        data.map((a, i) => (a.has_citation ? i : -1)).filter((i) => i >= 0),
      );
      setPreviewSelected(withCitations.size > 0 ? withCitations : new Set(data.map((_, i) => i)));
      setIsPreviewing(true);
    },
  });

  const importMutation = useMutation({
    mutationFn: async (annotations: AnnotationPreview[]) => {
      if (!paperId) throw new Error("Missing paperId");
      const searchParams: Record<string, string> = {};
      if (conceptId) searchParams.concept_id = conceptId;
      return apiClient
        .post(`api/papers/${paperId}/highlights/batch`, {
          json: { highlights: annotations },
          searchParams,
        })
        .json<BatchResponse>();
    },
    onMutate: async (annotations) => {
      await qc.cancelQueries({ queryKey: ["highlights", paperId] });
      const prev = qc.getQueryData<PaperHighlight[]>(["highlights", paperId]);
      if (prev) {
        const optimistic = annotations.map((a, i) => ({
          id: `temp-batch-${i}`,
          text: a.text,
          page: a.page,
          rects: a.rects,
          source: "imported" as const,
          has_citation: a.has_citation ?? null,
          created_at: new Date().toISOString(),
        }));
        qc.setQueryData(["highlights", paperId], [...prev, ...optimistic]);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["highlights", paperId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["highlights", paperId] });
      setIsPreviewing(false);
      setPreviews([]);
      setPreviewSelected(new Set());
    },
  });

  const importSelected = () => {
    const selected = previews.filter((_, i) => previewSelected.has(i));
    if (selected.length > 0) importMutation.mutate(selected);
  };

  const togglePreview = (index: number) => {
    setPreviewSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAllPreviews = () => setPreviewSelected(new Set(previews.map((_, i) => i)));
  const clearAllPreviews = () => setPreviewSelected(new Set());
  const cancelPreview = () => {
    setIsPreviewing(false);
    setPreviews([]);
    setPreviewSelected(new Set());
  };

  return {
    scan: () => scanMutation.mutate(),
    isScanning: scanMutation.isPending,
    isPreviewing,
    previews,
    previewSelected,
    togglePreview,
    selectAllPreviews,
    clearAllPreviews,
    cancelPreview,
    importSelected,
    isImporting: importMutation.isPending,
  };
}
